/**
 * gameResults.js
 * 
 * Saves the final result of an Auction game to the SQL database, through PersonalBackend.
 * Called once by the host's device when the game ends.
 */

import { callBackend } from "../../../config/backend";
import { rankTeams } from "./scoring";

/**
 * Identifies one game, so it's only ever saved once (room codes can be reused)
 * @param {Object} room     -   The room
 * @returns {String}        -   e.g. 'k7mq2x-1791683220000'
 */
export const getGameKey = (room) => {
    const started = room.startedAt ?? room.createdAt;
    return `${room.id}-${started?.toMillis?.() ?? 0}`;
};

/**
 * Saves the game's final standings
 * @param {Object} room     -   The room
 * @param {Array} teams     -   Teams in the room
 * @param {Object} settings -   Room settings
 * @param {Object} library  -   Room library
 * @param {Object} user     -   The host
 * @returns {Boolean}       -   True if saved (or already saved before)
 */
export const saveGameResult = async (room, teams, settings, library, user) => {
    const standings = rankTeams(teams, settings, library);
    const playerIds = [...new Set(standings.flatMap(({ team }) => team.members.map((member) => member.userID || member.uid)))];
    const winner = standings[0];

    try {
        // Saving the same game twice is fine: the backend ignores the repeat (e.g. the host's screen reloaded)
        await callBackend("/auction/game-results", { method: "POST", body: {
            gameKey         :   getGameKey(room),
            roomCode        :   room.id,
            libraryCode     :   library.code,
            libraryName     :   library.name,
            scoreAttribute  :   library.scoreAttribute,
            hostName        :   user?.displayName || null,
            startedAt       :   room.startedAt?.toDate?.().toISOString() ?? null,
            itemsSold       :   room.moviesSold || 0,
            itemCount       :   library.items.length,
            teamCount       :   standings.length,
            playerCount     :   playerIds.length,
            playerIds,
            winnerTeamName  :   winner?.team.name ?? null,
            winnerScore     :   winner?.total ?? null,
            teams           :   standings.map(({ rank, team, color, baseScore, bonus, bonuses, total, metRules, problems, spent, items, submitted, chosenByTeam }) => ({
                rank, teamName: team.name, color: color.key,
                members     :   team.members.map((member) => ({ userID: member.userID || member.uid, displayName: member.displayName || "" })),
                baseScore, bonus, bonuses, total, metRules, problems, spent, chosenByTeam,
                budgetLeft  :   team.budgetLeft ?? 0,
                items       :   items.map(({ name, score, values, soldPrice }, index) => ({ name, score, values, soldPrice, submitted: submitted.includes(items[index]) })),
            })),
            settings,
        } });
        return true;
    } catch (error) {
        console.error("Error saving game result:", error);
        return false;
    }
};
